import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  TextField, Button, Switch, FormControlLabel, FormControl, Select, MenuItem,
  IconButton, Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import DropdownTextField from '../form-components/DropdownTextField';
import { useCurrency } from '../../context/CurrencyContext';
import LocalText from '../common/LocalText';
import { hasRequiredName } from '../../utils/validators';
import { extractErrorMessage } from '../../utils/errorMessage';

const THUMBNAIL_MEDIA_TYPE_ID = 1; // image

// Same calculation ItemModal.jsx uses — kept identical on purpose so a package's price
// behaves exactly like every other item type's:
// Exclusive: entered price is net, tax is added on top — subtotal = price + tax.
// Inclusive: entered price already contains tax — net is back-calculated out of it.
const computePrice = (baseCost, taxRate, taxMode) => {
  const price = parseFloat(baseCost) || 0;
  const rate  = parseFloat(taxRate)  || 0;
  if (taxMode === 'Exclusive') {
    const taxAmount = price * rate / 100;
    return { subtotal: price + taxAmount, taxAmount, price };
  }
  const netBase   = rate > 0 ? price / (1 + rate / 100) : price;
  const taxAmount = price - netBase;
  return { subtotal: price, taxAmount, price: netBase };
};

const makeEmptyForm = () => ({
  nameKey: '',
  descriptionKey: '',
  nameLocalizations: [{ localeId: 'en_us', value: '' }],
  descriptionLocalizations: [{ localeId: 'en_us', value: '' }],
  thumbnailMedia: { mediaTypeId: THUMBNAIL_MEDIA_TYPE_ID, linkLocalizations: [{ localeId: 'en_us', mediaLink: '' }] },
  baseCost: '',
  taxId: '',
  taxMode: 'Exclusive',
  isAvailable: true,
});

/**
 * Create/edit form for a Package — mirrors ItemModal.jsx's conventions exactly:
 * - Name/Description/Thumbnail are all locale-aware via the same DropdownTextField component
 *   Item uses (locale picker per row, add/remove locales, required-hint, live URL validation
 *   for the thumbnail) — not a single hardcoded 'en_us' string.
 * - Currency is never hand-picked, resolved from the space's active currency (create) or the
 *   package's own saved currency (edit), same as Item.
 * - Tax category + Exclusive/Inclusive mode + the identical computePrice preview panel.
 * No embedded item picker — membership is managed on the separate PackageAddItems screen.
 */
export default function PackageModal({ open, onClose, onSave, initialData, taxCategories = [], onAddTaxClass, readOnly = false }) {
  const isEdit = Boolean(initialData?.packageId);
  const { activeCurrency, currencies } = useCurrency();

  // Edit: use the package's saved currency. Create: use the space's active currency —
  // same resolution order ItemModal.jsx uses, no manual currency picker either way.
  const pkgCurrencyId    = isEdit ? (initialData?.prices?.[0]?.currencyId ?? null) : null;
  const resolvedCurrency = (pkgCurrencyId ? currencies.find(c => c.id === pkgCurrencyId) : null) ?? activeCurrency;
  const symbol           = resolvedCurrency?.symbol ?? '';
  const decimalPlaces    = resolvedCurrency?.decimalPlaces ?? 2;
  const factor           = Math.pow(10, decimalPlaces);
  const priceStep        = decimalPlaces > 0 ? (1 / factor).toFixed(decimalPlaces) : '1';

  const [form, setForm] = useState(makeEmptyForm());
  const [saving, setSaving] = useState(false);
  const [thumbnailValid, setThumbnailValid] = useState(true);

  const [showTaxAdd, setShowTaxAdd] = useState(false);
  const [newTaxName, setNewTaxName] = useState('');
  const [newTaxRate, setNewTaxRate] = useState('');
  const [addingTax, setAddingTax] = useState(false);
  const [taxError, setTaxError] = useState('');

  useEffect(() => {
    if (!open) return;
    setShowTaxAdd(false); setNewTaxName(''); setNewTaxRate(''); setTaxError('');
    if (initialData) {
      setForm({
        nameKey: initialData.nameLocalizations?.key ?? '',
        descriptionKey: initialData.descriptionLocalizations?.key ?? '',
        nameLocalizations: initialData.nameLocalizations?.values?.length
          ? initialData.nameLocalizations.values
          : [{ localeId: 'en_us', value: '' }],
        descriptionLocalizations: initialData.descriptionLocalizations?.values?.length
          ? initialData.descriptionLocalizations.values
          : [{ localeId: 'en_us', value: '' }],
        thumbnailMedia: {
          mediaTypeId: THUMBNAIL_MEDIA_TYPE_ID,
          linkLocalizations: initialData.thumbnail?.linkLocalizations?.length
            ? initialData.thumbnail.linkLocalizations
            : [{ localeId: 'en_us', mediaLink: '' }],
        },
        baseCost: initialData.prices?.[0]?.baseCost != null
          ? String(initialData.prices[0].baseCost / Math.pow(10, currencies.find(c => c.id === initialData.prices[0].currencyId)?.decimalPlaces ?? 2))
          : '',
        taxId: initialData.tax?.categoryId ?? '',
        taxMode: initialData.tax?.isInclusive != null ? (initialData.tax.isInclusive ? 'Inclusive' : 'Exclusive') : 'Exclusive',
        isAvailable: initialData.isAvailable ?? true,
      });
    } else {
      setForm(makeEmptyForm());
    }
  }, [open, initialData, currencies]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const selectedTax = useMemo(
    () => taxCategories.find((t) => t.id === form.taxId) ?? null,
    [taxCategories, form.taxId]
  );
  const pricing = useMemo(
    () => computePrice(form.baseCost, selectedTax?.ratePct ?? 0, form.taxMode),
    [form.baseCost, selectedTax, form.taxMode]
  );

  const priceNum = parseFloat(form.baseCost);
  const isPriceValid = form.baseCost !== '' && !Number.isNaN(priceNum) && priceNum > 0;
  const isNameValid = hasRequiredName(form.nameLocalizations);
  const hasThumbnailValue = form.thumbnailMedia.linkLocalizations.some((l) => (l.mediaLink ?? '').trim().length > 0);
  const isThumbnailValid = readOnly || (hasThumbnailValue && thumbnailValid);
  const isValid = isNameValid && isThumbnailValid && isPriceValid;

  const handleAddTaxClass = async () => {
    if (!newTaxName.trim() || !newTaxRate) return;
    const ratePct = parseFloat(newTaxRate);
    if (Number.isNaN(ratePct) || ratePct < 0 || ratePct > 100) {
      setTaxError('Tax percentage must be between 0 and 100.');
      return;
    }
    setAddingTax(true); setTaxError('');
    try {
      const created = await onAddTaxClass({ name: newTaxName.trim(), ratePct });
      setForm((f) => ({ ...f, taxId: created.id }));
      setShowTaxAdd(false); setNewTaxName(''); setNewTaxRate('');
    } catch (err) {
      setTaxError(extractErrorMessage(err));
    } finally {
      setAddingTax(false);
    }
  };

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const baseCostMinor = Math.round((parseFloat(form.baseCost) || 0) * factor);
      const taxCategoryId = form.taxId || null;
      const taxIsInclusive = form.taxId ? form.taxMode === 'Inclusive' : null;

      if (isEdit) {
        await onSave({
          nameLocalizations: { key: form.nameKey, values: form.nameLocalizations },
          // null = leave the description unchanged. A package that HAD a description and now has none
          // sends an empty list, which tells the backend to clear it.
          descriptionLocalizations: form.descriptionLocalizations.some((l) => l.value?.trim())
            ? { key: form.descriptionKey || null, values: form.descriptionLocalizations }
            : (form.descriptionKey ? { key: form.descriptionKey, values: [] } : null),
          thumbnailMedia: form.thumbnailMedia,
          baseCost: baseCostMinor,
          currencyId: resolvedCurrency?.id,
          taxCategoryId,
          taxIsInclusive,
          isAvailable: form.isAvailable,
        });
      } else {
        await onSave({
          nameLocalizations: form.nameLocalizations,
          descriptionLocalizations: form.descriptionLocalizations.some((l) => l.value?.trim())
            ? form.descriptionLocalizations
            : null,
          thumbnailMedia: form.thumbnailMedia,
          baseCost: baseCostMinor,
          currencyId: activeCurrency?.id,
          taxCategoryId,
          taxIsInclusive,
          isAvailable: form.isAvailable,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
        {isEdit ? 'Edit Package' : 'Create Package'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <Box>
          <Typography fontSize={12} fontWeight={700} sx={{ mb: 0.5, color: '#475569' }}>Package Name *</Typography>
          <DropdownTextField
            label="Package name"
            values={form.nameLocalizations}
            onChange={(locs) => setForm((f) => ({ ...f, nameLocalizations: locs }))}
            attribute="value"
            required={!readOnly}
            disabled={readOnly}
          />
        </Box>

        <Box>
          <Typography fontSize={12} fontWeight={700} sx={{ mb: 0.5, color: '#475569' }}>Description</Typography>
          <DropdownTextField
            label="Description"
            values={form.descriptionLocalizations}
            onChange={(locs) => setForm((f) => ({ ...f, descriptionLocalizations: locs }))}
            attribute="value"
            multiline
            rows={3}
            disabled={readOnly}
          />
        </Box>

        <Box>
          <Typography fontSize={12} fontWeight={700} sx={{ mb: 0.5, color: '#475569' }}>Thumbnail *</Typography>
          <DropdownTextField
            label="Thumbnail URL"
            values={form.thumbnailMedia.linkLocalizations}
            onChange={(locs) => setForm((f) => ({ ...f, thumbnailMedia: { ...f.thumbnailMedia, linkLocalizations: locs } }))}
            attribute="mediaLink"
            urlValidationType="image"
            previewable
            mediaType="image"
            required={!readOnly}
            disabled={readOnly}
            onValidationChange={setThumbnailValid}
          />
        </Box>

        {/* ── Package Price — currency is never hand-picked, resolved from the space's
             active currency (create) or the package's own saved currency (edit) ─────── */}
        <Box>
          <Typography fontSize={12} fontWeight={700} sx={{ mb: 0.5, color: '#475569' }}>Package Price *</Typography>
          <TextField
            hiddenLabel type="number"
            value={form.baseCost} onChange={set('baseCost')} disabled={readOnly}
            required size="small" fullWidth
            inputProps={{ min: 0, step: priceStep }}
            InputProps={{ startAdornment: <Typography sx={{ color: '#64748b', mr: 0.5, fontSize: 13, fontWeight: 700 }}>{symbol}</Typography> }}
            placeholder="0.00"
            helperText={
              readOnly ? ''
              : form.baseCost === '' ? 'Required'
              : priceNum <= 0 ? 'Price must be greater than 0'
              : ''
            }
          />
        </Box>

        {/* ── Tax Category + Mode ──────────────────────────────────────────────────── */}
        <Box sx={{ bgcolor: '#f8fafc', p: 1.75, borderRadius: '12px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography fontSize={12} fontWeight={700} sx={{ mb: 0.5, color: '#475569' }}>Tax Category</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ flex: 1 }} disabled={readOnly}>
                  <Select value={form.taxId} onChange={set('taxId')} displayEmpty sx={{ '& .MuiSelect-select': { py: 1, fontSize: 12 } }}>
                    <MenuItem value=""><em style={{ fontSize: 12 }}><LocalText>None</LocalText></em></MenuItem>
                    {taxCategories.map((tc) => (
                      <MenuItem key={tc.id} value={tc.id} sx={{ fontSize: 12 }}><LocalText>{tc.name}</LocalText> ({tc.ratePct}%)</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!readOnly && onAddTaxClass && (
                  <IconButton
                    size="small"
                    onClick={() => { setShowTaxAdd((v) => !v); setTaxError(''); }}
                    title="Add Custom Tax Category"
                    sx={{ color: '#4f46e5', bgcolor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', width: 36, height: 36, flexShrink: 0, '&:hover': { bgcolor: '#e0e7ff' } }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Box>
              <Typography fontSize={12} fontWeight={700} sx={{ mb: 0.5, color: '#475569' }}>Tax Mode</Typography>
              <Box sx={{ display: 'flex', bgcolor: '#e2e8f0', p: '2px', borderRadius: '8px', border: '1px solid #cbd5e1', height: 36 }}>
                {['Exclusive', 'Inclusive'].map((mode) => (
                  <Box
                    key={mode}
                    onClick={() => !readOnly && setForm((f) => ({ ...f, taxMode: mode }))}
                    sx={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, cursor: readOnly ? 'default' : 'pointer', borderRadius: '6px', userSelect: 'none', transition: 'all 0.15s',
                      bgcolor: form.taxMode === mode ? 'white' : 'transparent',
                      color: form.taxMode === mode ? '#1e1b4b' : '#64748b',
                      boxShadow: form.taxMode === mode ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                    }}
                  >
                    <LocalText>{mode}</LocalText>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {!readOnly && onAddTaxClass && (
            <Collapse in={showTaxAdd}>
              <Box sx={{ bgcolor: 'rgba(238,242,255,0.85)', p: 1.75, borderRadius: '8px', border: '1px solid #e0e7ff' }}>
                <Typography fontSize={9} fontWeight={800} sx={{ color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BalanceOutlinedIcon sx={{ fontSize: 12 }} /> <LocalText>Create Custom Tax Category</LocalText>
                </Typography>
                {taxError && <Typography fontSize={11} color="error" sx={{ mb: 0.75 }}>{taxError}</Typography>}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 1.5 }}>
                  <TextField hiddenLabel value={newTaxName} onChange={(e) => setNewTaxName(e.target.value)} size="small" fullWidth placeholder="e.g. state luxury tax" />
                  <TextField
                    hiddenLabel type="number" value={newTaxRate} onChange={(e) => setNewTaxRate(e.target.value)}
                    size="small" sx={{ width: { xs: '100%', sm: 90 } }}
                    inputProps={{ min: 0, max: 100, step: '0.01' }} placeholder="0.00"
                    InputProps={{ endAdornment: <Typography fontSize={11} sx={{ color: '#94a3b8', fontWeight: 700 }}>%</Typography> }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.25 }}>
                  <Button onClick={() => { setShowTaxAdd(false); setTaxError(''); }} sx={{ fontSize: 11, color: '#64748b', textTransform: 'none' }}>
                    <LocalText>Cancel</LocalText>
                  </Button>
                  <Button
                    variant="contained" size="small" onClick={handleAddTaxClass}
                    disabled={addingTax || !newTaxName.trim() || !newTaxRate}
                    sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', px: 2, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' }, '&.Mui-disabled': { bgcolor: '#c7d2fe', color: 'white' } }}
                  >
                    <LocalText>Register Tax Category</LocalText>
                  </Button>
                </Box>
              </Box>
            </Collapse>
          )}
        </Box>

        {/* ── Price preview — same Base/Net/Tax/Mode breakdown ItemModal.jsx shows ──── */}
        {/* flexShrink: 0 — DialogContent is a flex column; without this, overflow:'hidden'
            below resets this Box's flex min-height from 'auto' to 0, so once the dialog's
            content is taller than the viewport, this is the one child the flexbox squeezes
            down to fit (clipping its own content) instead of the dialog scrolling normally. */}
        <Box sx={{ background: '#0f172a', borderRadius: '12px', p: 2.25, color: 'white', overflow: 'hidden', flexShrink: 0 }}>
          <Typography fontSize={9} fontWeight={800} sx={{ color: '#c7d2fe', textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5 }}>
            <LocalText>Package Costing</LocalText>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pb: 1.25, mb: 1.25, borderBottom: '1px solid rgba(99,102,241,0.25)' }}>
            <Typography fontSize={11.5} sx={{ color: '#e0e7ff' }}><LocalText>Customer Pays:</LocalText></Typography>
            <Typography fontSize={20} fontWeight={900} sx={{ color: '#34d399' }}>{symbol}{pricing.subtotal.toFixed(decimalPlaces)}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, fontFamily: 'monospace' }}>
            {[
              { key: 'base', label: 'Base', value: `${symbol}${(parseFloat(form.baseCost) || 0).toFixed(decimalPlaces)}` },
              { key: 'net', label: 'Net (excl. tax)', value: `${symbol}${pricing.price.toFixed(decimalPlaces)}` },
              { key: 'tax', label: 'Tax', value: `+${symbol}${pricing.taxAmount.toFixed(decimalPlaces)}`, color: '#fbbf24' },
              { key: 'mode', label: 'Mode', value: form.taxMode, color: '#a5b4fc' },
            ].map(({ key, label, value, color }) => (
              <Box key={key}>
                <Typography fontSize={8} sx={{ color: 'rgba(199,210,254,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>
                  <LocalText>{label}</LocalText>
                </Typography>
                <Typography fontSize={10} fontWeight={700} sx={{ color: color ?? 'white' }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <FormControlLabel
          control={<Switch checked={form.isAvailable} onChange={(e) => setForm((f) => ({ ...f, isAvailable: e.target.checked }))} disabled={readOnly} />}
          label={<Typography fontSize={12} fontWeight={700}>Available for purchase</Typography>}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}>Cancel</Button>
        {!readOnly && (
          <Button
            variant="contained" onClick={handleSave} disabled={!isValid || saving}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, borderRadius: '8px', background: '#0a326d', '&:hover': { background: '#0d3d85' } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
